import { useState } from 'react'
import { Save } from 'lucide-react'

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('general')
  const [formData, setFormData] = useState({
    companyName: 'Cybertection',
    companyEmail: 'support@cybertection.com',
    timezone: 'America/New_York',
    language: 'en',
    aiPersonality: 'professional',
    responseTemplate: 'thank_you',
    smsNumber: '410-701-2931',
    maxWaitTime: '5',
    escalationThreshold: '70',
  })

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = () => {
    console.log('Saving settings:', formData)
    // Integration point: Save to API
  }

  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'ai', label: 'AI Configuration' },
    { id: 'integrations', label: 'Integrations' },
    { id: 'team', label: 'Team' },
    { id: 'notifications', label: 'Notifications' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Settings</h1>
        <p className="text-blue-200 mt-1">Configure your support center</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-blue-500/30">
        <div className="flex space-x-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-cyber-600 text-cyber-600'
                  : 'border-transparent text-blue-200 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* General Settings */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Company Information</h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Company Name</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleChange('companyName', e.target.value)}
                  className="input-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Support Email</label>
                <input
                  type="email"
                  value={formData.companyEmail}
                  onChange={(e) => handleChange('companyEmail', e.target.value)}
                  className="input-base"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Timezone</label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => handleChange('timezone', e.target.value)}
                    className="input-base"
                  >
                    <option>America/New_York</option>
                    <option>America/Chicago</option>
                    <option>America/Denver</option>
                    <option>America/Los_Angeles</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Language</label>
                  <select
                    value={formData.language}
                    onChange={(e) => handleChange('language', e.target.value)}
                    className="input-base"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Business Hours</h3>
            </div>
            <div className="card-body space-y-3">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                <div key={day} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-200">{day}</span>
                  <div className="flex items-center space-x-2">
                    <input type="time" defaultValue="09:00" className="input-base w-32" />
                    <span className="text-blue-300/70">to</span>
                    <input type="time" defaultValue="18:00" className="input-base w-32" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI Configuration */}
      {activeTab === 'ai' && (
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">AI Agent Settings</h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">AI Personality</label>
                <select
                  value={formData.aiPersonality}
                  onChange={(e) => handleChange('aiPersonality', e.target.value)}
                  className="input-base"
                >
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="technical">Technical</option>
                </select>
                <p className="text-xs text-blue-300/70 mt-1">How the AI communicates with customers</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Response Template</label>
                <select
                  value={formData.responseTemplate}
                  onChange={(e) => handleChange('responseTemplate', e.target.value)}
                  className="input-base"
                >
                  <option value="thank_you">Thank You Response</option>
                  <option value="solution">Solution First</option>
                  <option value="escalate">Escalation Ready</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Escalation Confidence Threshold</label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData.escalationThreshold}
                    onChange={(e) => handleChange('escalationThreshold', e.target.value)}
                    className="flex-1"
                  />
                  <span className="text-lg font-bold text-cyber-600 w-12">{formData.escalationThreshold}%</span>
                </div>
                <p className="text-xs text-blue-300/70 mt-1">Escalate if AI confidence is below this threshold</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Integration Point: Claude API</h3>
            </div>
            <div className="card-body space-y-4">
              <p className="text-sm text-blue-200">Configure your Claude API key and settings here. This will be set up when you connect your integration.</p>
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">API Key</label>
                <input
                  type="password"
                  placeholder="sk-ant-..."
                  className="input-base"
                  disabled
                />
                <p className="text-xs text-blue-300/70 mt-1">Set via integration configuration</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integrations */}
      {activeTab === 'integrations' && (
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Connected Integrations</h3>
            </div>
            <div className="card-body space-y-3">
              {[
                { name: 'Claude AI', status: 'pending', icon: '🤖' },
                { name: 'TextNow SMS', status: 'pending', number: '410-701-2931', icon: '📱' },
                { name: 'Qdrant Vector DB', status: 'pending', icon: '🗄️' },
              ].map((integration, idx) => (
                <div key={idx} className="p-4 border border-blue-500/30 rounded-lg flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{integration.icon}</span>
                    <div>
                      <p className="font-medium text-white">{integration.name}</p>
                      {integration.number && <p className="text-xs text-blue-300/70">{integration.number}</p>}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      integration.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {integration.status === 'pending' ? 'Not Configured' : 'Connected'}
                    </span>
                    <button className="btn-secondary py-2 px-4">
                      {integration.status === 'pending' ? 'Configure' : 'Manage'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> You'll configure these integrations at the end. Each integration will provide setup instructions when you're ready to connect.
            </p>
          </div>
        </div>
      )}

      {/* Team */}
      {activeTab === 'team' && (
        <div className="space-y-6">
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h3 className="font-semibold text-white">Team Members</h3>
              <button className="btn-primary py-2 px-4 text-sm">Add Member</button>
            </div>
            <div className="card-body">
              <div className="divide-y divide-gray-200">
                {[
                  { name: 'Sarah Johnson', email: 'sarah@cybertection.com', role: 'Admin' },
                  { name: 'Mike Chen', email: 'mike@cybertection.com', role: 'Agent' },
                  { name: 'Lisa Brown', email: 'lisa@cybertection.com', role: 'Agent' },
                ].map((member, idx) => (
                  <div key={idx} className="py-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">{member.name}</p>
                      <p className="text-sm text-blue-200">{member.email}</p>
                    </div>
                    <select className="input-base w-32" defaultValue={member.role}>
                      <option>Admin</option>
                      <option>Agent</option>
                      <option>Trainer</option>
                      <option>Viewer</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      {activeTab === 'notifications' && (
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Notification Preferences</h3>
            </div>
            <div className="card-body space-y-4">
              {[
                { label: 'New Tickets', description: 'Notify when a new ticket is created' },
                { label: 'Escalations', description: 'Notify when a ticket is escalated' },
                { label: 'Course Completion', description: 'Notify when a student completes a course' },
                { label: 'Negative Feedback', description: 'Notify on negative customer feedback' },
              ].map((notif, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 border border-blue-500/30 rounded-lg">
                  <div>
                    <p className="font-medium text-white">{notif.label}</p>
                    <p className="text-sm text-blue-200">{notif.description}</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-5 h-5" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button onClick={handleSave} className="btn-primary flex items-center space-x-2">
          <Save size={20} />
          <span>Save Changes</span>
        </button>
      </div>
    </div>
  )
}
